import React from 'react';
import {twMerge} from 'tailwind-merge';

import {InstrumentedCenteredWaveLoader} from '../utility/InstrumentedLoader';
import {
  type TemplateSection,
  WorkspaceTemplateDisplayValues,
} from './WorkspaceTemplateDisplayValues';
import {WorkspaceTemplateRow} from './WorkspaceTemplateRow';
import {WorkspaceTemplateUpdatedAt} from './WorkspaceTemplateUpdatedAt';

type WorkspaceTemplateSettingsDisplayProps = {
  linePlotSections: TemplateSection[];
  workspaceLayoutSections: TemplateSection[];
  updatedAt?: string | null;
  isLoading?: boolean;
  headerActions?: React.ReactNode;
  /**
   * Where this component is displayed, which affects styling and behavior.
   * - 'drawer': Shows comparison values (current vs new) with row-style labels
   * - 'settingsPage': Shows only saved values with category-style labels
   * @default 'drawer'
   */
  displayContext?: 'drawer' | 'settingsPage';
  className?: string;
};

/**
 * Shared component for displaying workspace template settings.
 *
 * This component is used by both:
 * - WorkspaceTemplateContent (drawer) - for saving/updating templates
 * - UserSettingsWorkspaceTemplate (settings page) - for viewing/deleting templates
 */
export const WorkspaceTemplateSettingsDisplay = ({
  linePlotSections,
  workspaceLayoutSections,
  updatedAt,
  isLoading = false,
  headerActions,
  displayContext = 'drawer',
  className,
}: WorkspaceTemplateSettingsDisplayProps) => {
  const isDrawer = displayContext === 'drawer';

  return (
    <div className={twMerge('flex flex-col', className)}>
      <div
        className={twMerge(
          'flex items-center gap-8',
          isDrawer && 'mb-10 pb-10 border-b-[1px] border-b-moon-250'
        )}>
        <h2
          className={twMerge(
            isDrawer
              ? 'text-base font-semibold leading-[22px]'
              : 'font-[inherit] text-base font-semibold leading-[32px]'
          )}>
          Settings
        </h2>
        <WorkspaceTemplateUpdatedAt updatedAt={updatedAt} />
        {headerActions && <div className="ml-auto">{headerActions}</div>}
      </div>

      {isLoading ? (
        <div className="h-[5rem]">
          <InstrumentedCenteredWaveLoader
            name="workspace-template-settings"
            size="small"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-16">
          {/* Line Plot Settings Section */}
          {isDrawer ? (
            <div>
              <WorkspaceTemplateRow label="Line plot settings" />
              <WorkspaceTemplateDisplayValues
                templateSections={linePlotSections}
                showComparison
              />
            </div>
          ) : (
            <WorkspaceTemplateDisplayValues
              templateSections={linePlotSections}
              templateCategory="Line plots"
              showComparison={false}
            />
          )}

          {/* Workspace Layout Settings Section */}
          {isDrawer ? (
            <div data-test="workspace-template-layout-section">
              <WorkspaceTemplateRow label="Workspace layout settings" />
              <WorkspaceTemplateDisplayValues
                templateSections={workspaceLayoutSections}
                showComparison
              />
            </div>
          ) : (
            <WorkspaceTemplateDisplayValues
              templateSections={workspaceLayoutSections}
              templateCategory="Workspace layout"
              showComparison={false}
            />
          )}
        </div>
      )}
    </div>
  );
};
